import { useQuery } from '@tanstack/react-query'
import { useState, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  TrendingUp, TrendingDown, DollarSign, Package, Users, AlertCircle,
  Plus, ShoppingCart, Calendar, ArrowRight, FileText, CheckCircle2,
  Clock, Zap, Building2, Truck, UserPlus, Phone, Mail, Home, ClipboardList
} from 'lucide-react'
import api from '../services/api'
import OnboardingProgressKaart from '../components/dashboard/OnboardingProgressKaart'
import OfferteFinder from '../components/dashboard/OfferteFinder'
import { useAuthStore } from '../stores/authStore'
import ActieLijst from '../components/Homescreen/ActieLijst'
import SupplyChainRisksWidget from '../components/SupplyChainRisksWidget'
import ExpiringQuotesWidget from '../components/ExpiringQuotesWidget'
import MissingMaterialsWidget from '../components/procurement/MissingMaterialsWidget'
import { PageWrapper, PageHeader, SectionLabel, StatGrid, StatCard, Button } from '../components/ui'
// Charts removed - using simple trend indicators instead

function MijnTakenVandaagWidget({ navigate }) {
  const { t } = useTranslation()
  const { data } = useQuery({
    queryKey: ['crm-taken-vandaag'],
    queryFn: async () => {
      const res = await api.get('/crm/taken/vandaag')
      return res.data
    },
    refetchInterval: 60000,
  })

  const taken = data?.taken || []
  const vervallen = data?.vervallen_count || 0

  if (taken.length === 0) {
    return (
      <div className="r11-card p-4">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 className="w-5 h-5 text-green-500" />
          <h3 className="text-xs font-semibold text-muted uppercase tracking-wider">{t('dashboard.taken.title')}</h3>
        </div>
        <p className="text-sm text-green-600">{t('dashboard.taken.empty')}</p>
      </div>
    )
  }

  return (
    <div className="r11-card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-blue-500" />
          <h3 className="text-xs font-semibold text-muted uppercase tracking-wider">{t('dashboard.taken.title')}</h3>
          {vervallen > 0 && (
            <span className="badge badge-danger">{vervallen} vervallen</span>
          )}
        </div>
      </div>
      <div className="space-y-2">
        {taken.slice(0, 5).map(taak => (
          <div key={taak.id}
            onClick={() => taak.klant_id && navigate(`/klanten?select=${taak.klant_id}`)}
            className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${taak.is_vervallen ? 'bg-red-50 hover:bg-red-100' : 'bg-canvas hover:bg-gray-100'}`}
          >
            <span className="flex-shrink-0 text-muted">
              {(() => {
                const TaakIcon = taak.taak_type === 'BEL_TERUG' ? Phone : taak.taak_type === 'STUUR_EMAIL' ? Mail : taak.taak_type === 'BEZOEK' ? Home : ClipboardList
                return <TaakIcon className="w-4 h-4" />
              })()}
            </span>
            <div className="flex-1 min-w-0">
              <p className={`text-sm truncate ${taak.is_vervallen ? 'text-red-700 font-medium' : 'text-ink-2'}`}>{taak.titel}</p>
              <p className="text-xs text-muted truncate">{taak.klant_naam || ''}</p>
            </div>
            {taak.deadline && (
              <span className="text-xs text-faint flex-shrink-0">
                {new Date(taak.deadline).toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        ))}
      </div>
      {taken.length > 5 && (
        <button onClick={() => navigate('/taken')} className="w-full mt-2 text-xs text-blue-600 hover:underline text-center">
          Alle {taken.length} taken bekijken
        </button>
      )}
    </div>
  )
}

function OpenstaandeVorderingenWidget({ navigate }) {
  const { t } = useTranslation()
  const { data } = useQuery({
    queryKey: ['aging-report-widget'],
    queryFn: async () => {
      const res = await api.get('/facturen/aging-report')
      return res.data
    },
    staleTime: 5 * 60 * 1000,
  })

  const sam = data?.samenvatting || {}
  const periodes = data?.periodes || []
  const nietVervallen = periodes.find(p => p.dagen_range === '0')?.subtotaal || 0
  const kort = periodes.find(p => p.dagen_range === '1-30')?.subtotaal || 0
  const lang = periodes.filter(p => !['0', '1-30'].includes(p.dagen_range)).reduce((s, p) => s + (p.subtotaal || 0), 0)
  const totaal = sam.totaal_openstaand || 0

  return (
    <div className="r11-card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-orange-500" />
          <h3 className="text-xs font-semibold text-muted uppercase tracking-wider">Openstaande vorderingen</h3>
        </div>
        {(sam.totaal_vervallen || 0) > 0 && (
          <span className="badge badge-danger">
            {periodes.reduce((s, p) => s + (p.dagen_range !== '0' ? p.aantal : 0), 0)} vervallen
          </span>
        )}
      </div>
      <div className="text-2xl font-bold text-ink dark:text-white mb-2">
        {totaal > 0 ? new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(totaal) : '-'}
      </div>
      {totaal > 0 && (
        <div className="flex h-3 rounded-full overflow-hidden bg-gray-100 mb-3">
          {nietVervallen > 0 && <div className="bg-green-400" style={{ width: `${(nietVervallen / totaal) * 100}%` }} title={`Niet vervallen: ${nietVervallen}`} />}
          {kort > 0 && <div className="bg-orange-400" style={{ width: `${(kort / totaal) * 100}%` }} title={`0-30d: ${kort}`} />}
          {lang > 0 && <div className="bg-red-500" style={{ width: `${(lang / totaal) * 100}%` }} title={`30+d: ${lang}`} />}
        </div>
      )}
      <button onClick={() => navigate('/financials/aging-report')}
        className="text-xs text-orange-600 hover:text-orange-800 font-medium flex items-center gap-1">
        {t('dashboard.viewAgingReport')} <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  )
}

export default function DashboardPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [selectedPipelineStage, setSelectedPipelineStage] = useState(null)
  
  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: async () => {
      const response = await api.get('/dashboard/stats')
      return response.data
    },
    refetchInterval: 60000, // Refresh every minute
  })
  
  // Check setup status for empty state
  const { data: setupStatus } = useQuery({
    queryKey: ['setup', 'status'],
    queryFn: async () => {
      const response = await api.get('/setup/status')
      return response.data
    }
  })

  const { data: werkbladOrderSummary } = useQuery({
    queryKey: ['order-tracking-summary'],
    queryFn: async () => {
      const response = await api.get('/procurement/order-tracking/summary')
      return response.data
    },
    refetchInterval: 60000,
  })
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }
  
  const kpis = stats?.kpis || {}
  const pipeline = stats?.production_pipeline
  const recentProjects = stats?.recent_projects || []
  const notifications = stats?.notifications || []
  const hasPlanning = stats?.has_planning || false
  const tier = stats?.tier || 'STUDIO'
  
  // Empty state check: setup completed but 0 projects
  const isSetupCompleted = setupStatus?.is_setup_completed || false
  const hasProjects = recentProjects.length > 0 || (pipeline && Object.values(pipeline.stages || {}).some(count => count > 0))
  const showEmptyState = isSetupCompleted && !hasProjects
  
  return (
    <PageWrapper>
      <PageHeader
        eyebrow={t('dashboard.eyebrow', { defaultValue: 'Overzicht' })}
        title={t('dashboard.managementDashboard', { defaultValue: 'Mission Control' })}
        subtitle={t('dashboard.overview', { defaultValue: 'Overzicht van je bedrijf in één oogopslag' })}
      />

      {/* Offerte-finder: snel een offerte vinden + openen zonder klikketocht */}
      <OfferteFinder />

      {/* Onboarding progress kaart (verdwijnt als alles compleet) */}
      <OnboardingProgressKaart />

      {/* Actielijst — wat moet je vandaag doen? */}
      <ActieLijst gebruikerNaam={user?.gebruikersnaam || ''} />

      {/* DNA Integrity Banner is shown globally in Layout when DNA is incomplete */}
      <SupplyChainRisksWidget />
      
      {/* Empty State Banner - Only show if setup completed but no projects */}
      {showEmptyState && (
        <div className="r11-card r11-card-pad">
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-ink dark:text-white mb-1">{t('dashboard.welcome_message', { defaultValue: 'Welkom bij Orion!' })}</h2>
              <p className="text-muted mb-4">
                {t('dashboard.setup_completed_message', { defaultValue: 'Je setup is voltooid. Start je eerste project om te zien hoe alles werkt.' })}
              </p>
              <div className="flex flex-wrap gap-3 mb-4">
                <Button icon={Plus} onClick={() => navigate('/projecten')}>
                  {t('dashboard.create_first_project', { defaultValue: 'Nieuw Project Aanmaken (PDF Upload)' })}
                </Button>
                <Button as="a" href="/snelofferte" variant="ghost" size="sm" icon={Zap}>
                  {t('dashboard.quick_offer', { defaultValue: 'Of maak een snelofferte (2 min)' })}
                </Button>
              </div>
              <div className="text-sm text-muted">
                <p className="mb-1 flex items-center gap-1.5"><FileText className="w-4 h-4" aria-hidden="true" /> <strong className="text-ink dark:text-white">{t('dashboard.quick_start_guide', { defaultValue: 'Snelle Start Gids:' })}</strong></p>
                <ol className="list-decimal list-inside space-y-1 ml-4">
                  <li>{t('dashboard.quick_start_step1', { defaultValue: 'Upload een PDF van je eerste project' })}</li>
                  <li>{t('dashboard.quick_start_step2', { defaultValue: 'Orion analyseert automatisch de materialen' })}</li>
                  <li>{t('dashboard.quick_start_step3', { defaultValue: 'Genereer je eerste offerte' })}</li>
                </ol>
              </div>
            </div>
            <div className="hidden md:block flex-shrink-0">
              <Building2 className="w-28 h-28 text-gray-100 dark:text-gray-700" aria-hidden="true" strokeWidth={1.5} />
            </div>
          </div>
        </div>
      )}
      
      {/* KPI Cards Row */}
      <div>
        <SectionLabel>{t('dashboard.kerncijfers', { defaultValue: 'Kerncijfers' })}</SectionLabel>
        <StatGrid cols={4}>
          <StatCard
            label={t('dashboard.stats.omzetDezeMaand', { defaultValue: 'Omzet deze maand' })}
            value={`€${(kpis.omzet_deze_maand || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            icon={DollarSign}
            subtitle={
              kpis.omzet_trend_percentage != null ? (
                <span className={`inline-flex items-center gap-0.5 ${kpis.omzet_trend_percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {kpis.omzet_trend_percentage >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                  {Math.abs(kpis.omzet_trend_percentage).toFixed(1)}%
                </span>
              ) : undefined
            }
          />

          <StatCard
            label={t('dashboard.active_projects', { defaultValue: 'Actieve Projecten' })}
            value={(kpis.actieve_projecten || 0).toLocaleString('nl-NL')}
            icon={Package}
          />

          {hasPlanning && kpis.werkplaats_belasting && (
            <StatCard
              label={t('dashboard.workshop_load', { defaultValue: 'Werkplaats Belasting' })}
              value={`${kpis.werkplaats_belasting.percentage.toFixed(1)}%`}
              icon={Clock}
              subtitle={`${kpis.werkplaats_belasting.estimated_hours.toFixed(1)}h / ${kpis.werkplaats_belasting.available_hours}h`}
              progress={kpis.werkplaats_belasting.percentage}
              tone={kpis.werkplaats_belasting.percentage > 80 ? 'warning' : 'default'}
            />
          )}

          {!hasPlanning && (
            <StatCard
              label={t('dashboard.material_order_status', { defaultValue: 'Bestelstatus Materialen' })}
              value={t('common.active', { defaultValue: 'Actief' })}
              icon={ShoppingCart}
              onClick={() => navigate('/procurement')}
            />
          )}

          <StatCard
            label={t('dashboard.pending_offers', { defaultValue: 'Wachtende Offertes' })}
            value={(kpis.wachtende_offertes || 0).toLocaleString('nl-NL')}
            icon={FileText}
            onClick={() => navigate('/offertes')}
          />
        </StatGrid>
      </div>

      {/* Werkbladen in bestelling widget */}
      {werkbladOrderSummary && (werkbladOrderSummary.in_bestelling > 0 || werkbladOrderSummary.over_tijd > 0) && (
        <div
          onClick={() => navigate('/procurement/purchasing')}
          className="r11-card p-6 border-l-4 border-amber-500 cursor-pointer hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-50 text-amber-600">
                <Truck className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-ink">
                  {werkbladOrderSummary.in_bestelling} {t('dashboard.werkbladenInBestelling', { defaultValue: 'Werkbladen in bestelling' })}
                  {werkbladOrderSummary.over_tijd > 0 && (
                    <span className="text-red-600 ml-1">
                      ({werkbladOrderSummary.over_tijd} {t('dashboard.overTijd', { defaultValue: 'over tijd' })})
                    </span>
                  )}
                </h3>
                <p className="text-sm text-muted">
                  {t('dashboard.werkbladenWidgetSubtitle', { defaultValue: 'Klik om open orders te bekijken en op te volgen' })}
                </p>
              </div>
            </div>
            {werkbladOrderSummary.over_tijd > 0 && (
              <AlertCircle className="w-6 h-6 text-red-600" />
            )}
          </div>
        </div>
      )}

      {/* CRM Taken Vandaag + Expiring Quotes + Openstaande vorderingen */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <MijnTakenVandaagWidget navigate={navigate} />
        <ExpiringQuotesWidget />
        <OpenstaandeVorderingenWidget navigate={navigate} />
      </div>
      
      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Production Pipeline (only for Academy/Master) */}
          {hasPlanning && pipeline && (
            <ProductionPipeline
              pipeline={pipeline}
              selectedStage={selectedPipelineStage}
              onStageClick={setSelectedPipelineStage}
            />
          )}
          
          {/* Recent Projects */}
          <RecentProjects
            projects={recentProjects}
            filterStage={selectedPipelineStage}
          />
        </div>
        
        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* System Notifications – bovenaan (planning-kritiek eerst) */}
          <SystemNotifications notifications={notifications} />
          
          {/* Quick Actions */}
          <QuickActions tier={tier} hasPlanning={hasPlanning} />
          
          {/* Phase 4: Missing Materials – vaak geïmporteerde Diversen niet in bibliotheek */}
          <MissingMaterialsWidget />
        </div>
      </div>
    </PageWrapper>
  )
}

// Production Pipeline Component
function ProductionPipeline({ pipeline, selectedStage, onStageClick }) {
  const stages = ['Engineering', 'Zagen', 'Afplakken', 'CNC', 'Montage', 'Klaar']
  const maxCount = Math.max(...stages.map((s) => pipeline.stages[s] || 0), 1)

  return (
    <div className="r11-card p-6">
      <h2 className="text-xs font-semibold text-muted uppercase tracking-wider mb-4">Productie Pipeline</h2>

      {/* Flow-diagram: stadia als verbonden nodes met pijl-connectoren (strak, monochroom) */}
      <div className="flex items-stretch gap-1.5 overflow-x-auto pb-1">
        {stages.map((name, i) => {
          const count = pipeline.stages[name] || 0
          const isSelected = selectedStage === name
          const pct = Math.round((count / maxCount) * 100)
          return (
            <Fragment key={name}>
              <button
                onClick={() => onStageClick(isSelected ? null : name)}
                title={`${name}: ${count} projecten`}
                className={`flex-1 min-w-[92px] text-left rounded-control border p-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 ${
                  isSelected
                    ? 'border-accent bg-accent/5'
                    : 'border-line dark:border-gray-700 hover:border-accent/40'
                }`}
              >
                <div className={`text-2xl font-bold font-mono tracking-tight ${isSelected ? 'text-accent' : 'text-ink dark:text-white'}`}>{count}</div>
                <div className="text-xs text-muted mt-0.5 truncate">{name}</div>
                <div className="mt-2 h-1 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${isSelected ? 'bg-accent' : 'bg-gray-300 dark:bg-gray-600'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </button>
              {i < stages.length - 1 && (
                <ArrowRight className="w-4 h-4 text-line dark:text-gray-600 self-center flex-shrink-0" aria-hidden="true" />
              )}
            </Fragment>
          )
        })}
      </div>

      {/* Projecten in de geselecteerde fase */}
      {selectedStage && pipeline.projects_by_stage[selectedStage] && (
        <div className="mt-4 pt-4 border-t border-line dark:border-gray-700">
          <SectionLabel>Projecten in {selectedStage}</SectionLabel>
          <div className="space-y-2">
            {pipeline.projects_by_stage[selectedStage].map((project) => (
              <div
                key={project.id}
                className="p-3 bg-canvas dark:bg-gray-900 rounded-control hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                onClick={() => { window.location.href = `/projecten/${project.id}` }}
              >
                <div className="font-medium text-sm text-ink dark:text-white">{project.name}</div>
                {project.klant_naam && (
                  <div className="text-xs text-muted">{project.klant_naam}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Recent Projects Component
function RecentProjects({ projects, filterStage }) {
  const { t } = useTranslation()
  const filteredProjects = filterStage 
    ? projects.filter(p => p.status === filterStage)
    : projects
  
  return (
    <div className="r11-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xs font-semibold text-muted uppercase tracking-wider">{t('dashboard.recentProjects')}</h2>
        <button
          onClick={() => window.location.href = '/projecten'}
          className="text-sm text-accent hover:text-accent-700 flex items-center gap-1"
        >
          {t('dashboard.viewAll')}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      
      {filteredProjects.length > 0 ? (
        <div className="space-y-3">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="p-4 border border-line dark:border-gray-700 rounded-lg hover:bg-canvas cursor-pointer transition-colors"
              onClick={() => window.location.href = `/projecten/${project.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="font-medium text-ink">{project.name}</div>
                  {project.klant_naam && (
                    <div className="text-sm text-muted mt-1">{project.klant_naam}</div>
                  )}
                </div>
                <span className={`badge ${
                  project.status === 'In Productie' ? 'badge-active' :
                  project.status === 'Geaccepteerd' ? 'badge-success' :
                  'badge-concept'
                }`}>
                  {project.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="r11-empty">
          <Package className="r11-empty__icon" />
          <p className="r11-empty__title">{t('dashboard.noProjectsFound')}</p>
        </div>
      )}
    </div>
  )
}

// Quick Actions Component
function QuickActions({ tier, hasPlanning }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  
  const actions = [
    { label: 'Nieuw Project', icon: Plus, onClick: () => navigate('/projecten') },
    { label: 'Nieuwe Klant', icon: UserPlus, onClick: () => navigate('/klanten?new=1') },
    { label: 'Materiaal Bestellen', icon: ShoppingCart, onClick: () => navigate('/procurement') },
  ]

  if (hasPlanning) {
    actions.push({ label: 'Team Planning', icon: Calendar, onClick: () => navigate('/installation-scheduling') })
  }

  return (
    <div className="r11-card p-6">
      <h2 className="text-xs font-semibold text-muted uppercase tracking-wider mb-4">Snelle Acties</h2>
      <div className="space-y-2">
        {actions.map((action) => {
          const ActionIcon = action.icon
          return (
            <button
              key={action.label}
              onClick={action.onClick}
              className="w-full flex items-center gap-3 rounded-control border border-line dark:border-gray-700 bg-surface dark:bg-gray-800 px-4 py-3 text-sm font-medium text-ink dark:text-white transition-colors hover:border-accent/40 hover:bg-gray-50 dark:hover:bg-gray-700/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
            >
              <ActionIcon className="w-5 h-5 text-muted" aria-hidden="true" />
              {action.label}
            </button>
          )
        })}
      </div>

      {/* Studio-tip: zachte accent-tint i.p.v. een volle gekleurde box */}
      {tier === 'STUDIO' && (
        <div className="mt-6 p-4 rounded-control bg-accent/5 border border-accent/20">
          <div className="flex items-center gap-2 mb-1">
            <Zap className="w-5 h-5 text-accent" aria-hidden="true" />
            <span className="font-semibold text-ink dark:text-white">{t('dashboard.studioTip.title')}</span>
          </div>
          <p className="text-sm text-muted">
            {t('dashboard.studioTip.upgradeAcademy')}
          </p>
        </div>
      )}
    </div>
  )
}

// System Notifications Component
function SystemNotifications({ notifications }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'warning': return AlertCircle
      case 'error': return AlertCircle
      case 'info': return Clock
      case 'success': return CheckCircle2
      default: return AlertCircle
    }
  }
  
  const getNotificationColor = (type) => {
    switch (type) {
      case 'warning': return 'bg-yellow-50 border-yellow-200 text-yellow-800'
      case 'error': return 'bg-red-50 border-red-200 text-red-800'
      case 'info': return 'bg-blue-50 border-blue-200 text-blue-800'
      case 'success': return 'bg-green-50 border-green-200 text-green-800'
      default: return 'bg-gray-50 border-gray-200 text-gray-800'
    }
  }
  
  return (
    <div className="r11-card p-6">
      <h2 className="text-xs font-semibold text-muted uppercase tracking-wider mb-4">{t('dashboard.systemNotifications')}</h2>
      
      {notifications.length > 0 ? (
        <div className="space-y-3">
          {notifications.map((notification, index) => {
            const Icon = getNotificationIcon(notification.type)
            return (
              <div
                key={index}
                className={`p-4 rounded-lg border ${getNotificationColor(notification.type)}`}
              >
                <div className="flex items-start gap-3">
                  <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium mb-1">{notification.title}</div>
                    <div className="text-sm opacity-90">{notification.message}</div>
                    {notification.action && (
                      <button
                        onClick={() => navigate(notification.action)}
                        className="mt-2 text-sm font-medium underline"
                      >
                        {t('dashboard.viewArrow')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="r11-empty">
          <CheckCircle2 className="w-14 h-14 mb-2 text-green-500" />
          <p className="r11-empty__title">{t('dashboard.notifications.empty')}</p>
          <p className="r11-empty__sub">{t('dashboard.notifications.allClear')}</p>
        </div>
      )}
    </div>
  )
}
